import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { API, useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Plus, Trash2, Crown, LayoutGrid, List, Check, CalendarClock, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor, useSensor, useSensors,
  closestCorners, useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { formatDateBR, todayCivilSP } from "@/lib/dates";
import TaskActivity from "@/components/TaskActivity";
import { phase1Api } from "@/lib/phase1Api";
import { DADOS } from "../lib/larguras";

const EMPTY_FORM = { title: "", descricao: "", data_desejada: "", data_limite: "", stage_id: "", prioridade: "", responsavel_id: "", processo_id: "" };

// Paleta curada — deve espelhar STAGE_COLORS do backend.
const STAGE_COLORS = [
  { hex: "#003882", label: "Azul" },
  { hex: "#6685AD", label: "Aço" },
  { hex: "#335480", label: "Ardósia" },
  { hex: "#0f766e", label: "Verde" },
  { hex: "#b45309", label: "Âmbar" },
  { hex: "#9f1239", label: "Vinho" },
  { hex: "#6d28d9", label: "Roxo" },
  { hex: "#374151", label: "Grafite" },
];
const FALLBACK_COLORS = ["#6685AD", "#003882", "#b45309", "#0f766e"];

function stageColor(stage, index) {
  return stage.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  const today = todayCivilSP();
  return dateStr < today;
}

const PRIORITY_LABELS = { baixa: "Baixa", normal: "Normal", alta: "Alta", urgente: "Urgente" };
const priorityClass = { baixa: "bg-[#F4F7FB] text-[#6685AD]", normal: "bg-[#e5edf5] text-[#335480]", alta: "bg-amber-50 text-amber-700", urgente: "bg-[#003882] text-white" };

// ─── Card (sortable) ──────────────────────────────────────────────────────
function KanbanCard({ card, onOpen, onToggleConcluido, dragging }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.card_id,
    data: { type: "card", stageId: card.stage_id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };

  const fatal = card.data_limite;
  const overdue = !card.concluido && isOverdue(fatal);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onOpen(card)}
      data-testid={`kanban-card-${card.card_id}`}
      className={`group bg-white border rounded-lg p-3 cursor-grab active:cursor-grabbing select-none transition-colors ${
        dragging ? "border-[#003882]" : overdue ? "border-[#003882]" : "border-[#D1DEEE] hover:border-[#003882]"
      }`}
    >
      <div className="flex items-start gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); onToggleConcluido(card); }}
          aria-label={card.concluido ? "Reabrir card" : "Concluir card"}
          className={`mt-0.5 w-4 h-4 rounded-sm border flex-shrink-0 flex items-center justify-center transition-colors ${
            card.concluido ? "bg-[#003882] border-[#003882]" : "border-[#6685AD] hover:border-[#003882] bg-white"
          }`}
        >
          {card.concluido && <Check size={12} className="text-white" strokeWidth={3} />}
        </button>
        <p className={`text-sm font-medium leading-snug flex-1 ${card.concluido ? "text-[#6685AD] line-through" : "text-[#001533]"}`}>
          {card.title}
        </p>
      </div>
      {(card.data_desejada || fatal) && (
        <div className="flex flex-wrap items-center gap-2 mt-2 pl-6">
          {card.data_desejada && (
            <span className="inline-flex items-center gap-1 text-xs text-[#335480]">
              <CalendarClock size={12} /> {formatDateBR(card.data_desejada)}
            </span>
          )}
          {fatal && (
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded ${
              overdue ? "bg-[#003882] text-white" : "bg-[#e5edf5] text-[#003882]"
            }`}>
              <AlertCircle size={12} /> Fatal {formatDateBR(fatal)}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-2 pl-6">
        {card.prioridade && <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${priorityClass[card.prioridade] || priorityClass.normal}`}>{PRIORITY_LABELS[card.prioridade] || card.prioridade}</span>}
        {(card.responsavel_nome || card.responsavel?.name) && <span className="text-xs text-[#6685AD] truncate">{card.responsavel_nome || card.responsavel.name}</span>}
        {(card.processo_id || card.intimacao_id) && <span className="text-xs text-[#003882]">Vinculado</span>}
      </div>
    </div>
  );
}

// ─── Stage color dot (PRO: popover de paleta) ─────────────────────────────
function StageColorDot({ stage, color, isPro, onChangeColor }) {
  const [open, setOpen] = useState(false);
  const dot = (
    <span
      className="w-3 h-3 rounded-full flex-shrink-0 inline-block"
      style={{ backgroundColor: color }}
      aria-hidden="true"
    />
  );
  if (!isPro) return dot;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label={`Alterar cor da etapa ${stage.name}`}
          data-testid={`stage-color-${stage.stage_id}`}
          className="p-1 -m-1 rounded hover:bg-[#e5edf5] transition-colors"
        >
          {dot}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="grid grid-cols-4 gap-1.5">
          {STAGE_COLORS.map((c) => (
            <button
              key={c.hex}
              title={c.label}
              aria-label={c.label}
              onClick={() => { onChangeColor(stage, c.hex); setOpen(false); }}
              className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${
                color === c.hex ? "ring-2 ring-offset-2 ring-[#003882]" : ""
              }`}
              style={{ backgroundColor: c.hex }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Column ───────────────────────────────────────────────────────────────
function KanbanColumn({ stage, color, cards, onOpen, onToggleConcluido, onAddCard, isPro, onChangeColor }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.stage_id, data: { type: "stage" } });
  return (
    <div className="flex flex-col w-72 flex-shrink-0">
      <div className="flex items-center justify-between px-1 mb-3">
        <h3 className="text-sm font-semibold text-[#001533] flex items-center gap-2">
          <StageColorDot stage={stage} color={color} isPro={isPro} onChangeColor={onChangeColor} />
          {stage.name}
          <span className="text-[#6685AD] font-normal">{cards.length}</span>
        </h3>
        <button
          onClick={() => onAddCard(stage.stage_id)}
          aria-label={`Adicionar card em ${stage.name}`}
          className="text-[#6685AD] hover:text-[#003882] transition-colors p-1"
        >
          <Plus size={16} />
        </button>
      </div>
      <div
        ref={setNodeRef}
        className={`flex flex-col gap-2 rounded-lg p-2 min-h-[120px] flex-1 transition-colors ${
          isOver ? "bg-[#e5edf5]" : "bg-[#F4F7FB]"
        }`}
      >
        <SortableContext items={cards.map((c) => c.card_id)} strategy={verticalListSortingStrategy}>
          {cards.map((card) => (
            <KanbanCard key={card.card_id} card={card} onOpen={onOpen} onToggleConcluido={onToggleConcluido} />
          ))}
        </SortableContext>
        {cards.length === 0 && (
          <button
            onClick={() => onAddCard(stage.stage_id)}
            className="text-sm text-[#6685AD] hover:text-[#003882] py-6 rounded-lg border border-dashed border-[#D1DEEE] hover:border-[#003882] transition-colors"
          >
            + Novo card
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────
export default function KanbanPage() {
  const { user } = useAuth();
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState(null);
  const [stages, setStages] = useState([]);
  const [cards, setCards] = useState([]);
  const [plan, setPlan] = useState("free");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("cards"); // "cards" | "lista"
  const [activeCard, setActiveCard] = useState(null); // card sendo arrastado
  const [showCardModal, setShowCardModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null); // null = criar
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showBoardModal, setShowBoardModal] = useState(false);
  const [boardName, setBoardName] = useState("");
  const [members, setMembers] = useState([]);
  const [activity, setActivity] = useState({ comments: [], history: [], loading: false, partial: false });
  const [commentPage, setCommentPage] = useState(1);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadBoards = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kanban/boards`, { withCredentials: true });
      setBoards(res.data.boards);
      setPlan(res.data.plan);
      return res.data.boards;
    } catch {
      toast.error("Erro ao carregar quadros");
      return [];
    }
  }, []);

  const loadBoard = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/kanban/boards/${id}`, { withCredentials: true });
      setStages(res.data.stages);
      setCards(res.data.cards);
      setMembers(res.data.members || []);
      setPlan(res.data.plan);
      return res.data;
    } catch {
      toast.error("Erro ao carregar o quadro");
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const bs = await loadBoards();
      const requestedCardId = new URLSearchParams(window.location.search).get("card_id");
      let targetBoardId = requestedCardId ? null : bs[0]?.board_id;
      if (requestedCardId) {
        try {
          const located = await phase1Api.findCardAcrossBoards(requestedCardId);
          if (located) targetBoardId = located.board.board_id;
          else toast.error("Tarefa não encontrada nos quadros deste escritório");
        } catch {
          toast.error("Não foi possível localizar a tarefa");
        }
      }
      if (targetBoardId) {
        const id = targetBoardId;
        setBoardId(id);
        const boardData = await loadBoard(id);
        const requestedCard = requestedCardId && boardData?.cards?.find((card) => card.card_id === requestedCardId);
        if (requestedCard) openEdit(requestedCard);
      }
      setLoading(false);
    })();
  }, [loadBoards, loadBoard]);

  const cardsByStage = useMemo(() => {
    const map = {};
    for (const s of stages) map[s.stage_id] = [];
    for (const c of [...cards].sort((a, b) => a.order - b.order)) {
      (map[c.stage_id] = map[c.stage_id] || []).push(c);
    }
    return map;
  }, [stages, cards]);

  // ─── Drag handlers (optimistic) ─────────────────────────────────────────
  const handleDragStart = ({ active }) => {
    setActiveCard(cards.find((c) => c.card_id === active.id) || null);
  };

  const resolveTarget = (over) => {
    if (!over) return null;
    if (over.data?.current?.type === "stage") return { stageId: over.id, index: null };
    const overCard = cards.find((c) => c.card_id === over.id);
    if (!overCard) return null;
    const list = cardsByStage[overCard.stage_id] || [];
    return { stageId: overCard.stage_id, index: list.findIndex((c) => c.card_id === over.id) };
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveCard(null);
    const target = resolveTarget(over);
    if (!target) return;
    const card = cards.find((c) => c.card_id === active.id);
    if (!card) return;

    const destList = (cardsByStage[target.stageId] || []).filter((c) => c.card_id !== card.card_id);
    const destIndex = target.index == null ? destList.length : Math.min(target.index, destList.length);

    if (card.stage_id === target.stageId) {
      const currentList = cardsByStage[card.stage_id] || [];
      const currentIndex = currentList.findIndex((c) => c.card_id === card.card_id);
      if (currentIndex === destIndex) return; // nada mudou
    }

    // Otimista: aplica local, chama API, rollback em erro
    const prev = cards;
    const next = cards.map((c) => ({ ...c }));
    const moved = next.find((c) => c.card_id === card.card_id);
    moved.stage_id = target.stageId;
    // reordena localmente
    const rebuilt = [];
    for (const s of stages) {
      const inStage = next
        .filter((c) => c.stage_id === s.stage_id && c.card_id !== moved.card_id)
        .sort((a, b) => a.order - b.order);
      if (s.stage_id === target.stageId) inStage.splice(destIndex, 0, moved);
      inStage.forEach((c, i) => { c.order = i; rebuilt.push(c); });
    }
    setCards(rebuilt);

    try {
      await axios.post(
        `${API}/kanban/cards/${card.card_id}/move`,
        { stage_id: target.stageId, order: destIndex },
        { withCredentials: true },
      );
    } catch {
      setCards(prev);
      toast.error("Não foi possível mover o card");
    }
  };

  // ─── Card CRUD ──────────────────────────────────────────────────────────
  const openCreate = (stageId) => {
    setEditingCard(null);
    setForm({ ...EMPTY_FORM, stage_id: stageId || stages[0]?.stage_id || "" });
    setShowCardModal(true);
  };

  const openEdit = (card) => {
    setEditingCard(card);
    setForm({
      title: card.title,
      descricao: card.descricao || "",
      data_desejada: card.data_desejada || "",
      data_limite: card.data_limite || "",
      stage_id: card.stage_id,
      prioridade: card.prioridade || "",
      responsavel_id: card.responsavel_id || "",
      processo_id: card.processo_id || "",
    });
    setShowCardModal(true);
    setCommentPage(1);
    setActivity({ comments: [], history: [], loading: true, partial: false });
    phase1Api.getCardActivity(card.card_id, { commentsPage: 1 })
      .then((result) => setActivity({ ...result, loading: false, partial: result.commentsError || result.historyError }))
      .catch(() => setActivity({ comments: [], history: [], loading: false, partial: true }));
  };

  const saveCard = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editingCard) {
        const res = await axios.patch(
          `${API}/kanban/cards/${editingCard.card_id}`,
          {
            title: form.title,
            descricao: form.descricao,
            data_desejada: form.data_desejada,
            data_limite: form.data_limite,
            ...(form.prioridade ? { prioridade: form.prioridade } : {}),
            responsavel_id: form.responsavel_id || null,
            processo_id: form.processo_id || null,
          },
          { withCredentials: true },
        );
        setCards((cs) => cs.map((c) => (c.card_id === res.data.card_id ? res.data : c)));
        toast.success("Card atualizado");
      } else {
        const res = await axios.post(
          `${API}/kanban/boards/${boardId}/cards`,
          {
            title: form.title,
            descricao: form.descricao || null,
            stage_id: form.stage_id || null,
            data_desejada: form.data_desejada || null,
            data_limite: form.data_limite || null,
            prioridade: form.prioridade || "normal",
            responsavel_id: form.responsavel_id || null,
            processo_id: form.processo_id || null,
          },
          { withCredentials: true },
        );
        setCards((cs) => [...cs, res.data]);
        toast.success("Card criado");
      }
      setShowCardModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar card");
    } finally {
      setSaving(false);
    }
  };

  const addComment = async (text) => {
    if (!editingCard) return;
    try {
      await phase1Api.addComment(editingCard.card_id, text);
      const result = await phase1Api.getCardActivity(editingCard.card_id, { commentsPage: 1 });
      setActivity({ ...result, loading: false, partial: result.commentsError || result.historyError });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível adicionar o comentário");
    }
  };

  const editComment = async (commentId, text) => {
    try {
      await phase1Api.updateComment(editingCard.card_id, commentId, text);
      const result = await phase1Api.getCardActivity(editingCard.card_id, { commentsPage: 1 });
      setCommentPage(1);
      setActivity({ ...result, loading: false, partial: result.commentsError || result.historyError });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível editar o comentário");
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await phase1Api.deleteComment(editingCard.card_id, commentId);
      const result = await phase1Api.getCardActivity(editingCard.card_id, { commentsPage: 1 });
      setCommentPage(1);
      setActivity({ ...result, loading: false, partial: result.commentsError || result.historyError });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível remover o comentário");
    }
  };

  const loadMoreComments = async () => {
    if (!editingCard) return;
    const nextPage = commentPage + 1;
    try {
      const result = await phase1Api.getCardActivity(editingCard.card_id, { commentsPage: nextPage });
      setCommentPage(nextPage);
      setActivity((current) => ({ ...current, ...result, comments: [...current.comments, ...result.comments], loading: false, partial: result.commentsError || result.historyError }));
    } catch { toast.error("Não foi possível carregar mais comentários"); }
  };

  const deleteCard = async () => {
    if (!editingCard) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/kanban/cards/${editingCard.card_id}`, { withCredentials: true });
      setCards((cs) => cs.filter((c) => c.card_id !== editingCard.card_id));
      setShowCardModal(false);
      toast.success("Card removido");
    } catch {
      toast.error("Erro ao remover card");
    } finally {
      setSaving(false);
    }
  };

  const toggleConcluido = async (card) => {
    const prev = cards;
    setCards((cs) => cs.map((c) => (c.card_id === card.card_id ? { ...c, concluido: !c.concluido } : c)));
    try {
      await axios.patch(
        `${API}/kanban/cards/${card.card_id}`,
        { concluido: !card.concluido },
        { withCredentials: true },
      );
    } catch {
      setCards(prev);
      toast.error("Erro ao atualizar card");
    }
  };

  // ─── Board CRUD ─────────────────────────────────────────────────────────
  const createBoard = async (e) => {
    e.preventDefault();
    if (!boardName.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/kanban/boards`, { name: boardName }, { withCredentials: true });
      setShowBoardModal(false);
      setBoardName("");
      const bs = await loadBoards();
      setBoardId(res.data.board_id);
      await loadBoard(res.data.board_id);
      toast.success("Quadro criado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar quadro");
    } finally {
      setSaving(false);
    }
  };

  const switchBoard = async (id) => {
    setBoardId(id);
    await loadBoard(id);
  };

  const changeStageColor = async (stage, hex) => {
    const prev = stages;
    setStages((ss) => ss.map((s) => (s.stage_id === stage.stage_id ? { ...s, color: hex } : s)));
    try {
      await axios.patch(
        `${API}/kanban/stages/${stage.stage_id}`,
        { color: hex },
        { withCredentials: true },
      );
    } catch (err) {
      setStages(prev);
      toast.error(err.response?.data?.detail || "Erro ao alterar cor");
    }
  };

  const isPro = plan === "pro";

  // ─── Lista view ─────────────────────────────────────────────────────────
  const listaCards = useMemo(() => {
    const stageInfo = Object.fromEntries(
      stages.map((s, i) => [s.stage_id, { name: s.name, color: stageColor(s, i) }])
    );
    return [...cards]
      .sort((a, b) => {
        if (!!a.data_limite !== !!b.data_limite) return a.data_limite ? -1 : 1;
        if (a.data_limite !== b.data_limite) return (a.data_limite || "").localeCompare(b.data_limite || "");
        return a.order - b.order;
      })
      .map((c) => ({
        ...c,
        stageName: stageInfo[c.stage_id]?.name || "—",
        stageColor: stageInfo[c.stage_id]?.color,
      }));
  }, [cards, stages]);

  return (
    <Layout>
      <div className={DADOS}>
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Quadros
            </h1>
            {boards.length > 1 ? (
              <Select value={boardId || ""} onValueChange={switchBoard}>
                <SelectTrigger className="w-52 border-[#D1DEEE]" data-testid="board-selector">
                  <SelectValue placeholder="Quadro" />
                </SelectTrigger>
                <SelectContent>
                  {boards.map((b) => (
                    <SelectItem key={b.board_id} value={b.board_id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              boards[0] && <span className="text-[#335480] text-base mt-1">{boards[0].name}</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* View toggle */}
            <div className="flex rounded-lg border border-[#D1DEEE] overflow-hidden">
              <button
                onClick={() => setView("cards")}
                data-testid="view-cards-btn"
                className={`flex items-center gap-1.5 px-3 h-9 text-sm font-medium transition-colors ${
                  view === "cards" ? "bg-[#003882] text-white" : "bg-white text-[#335480] hover:bg-[#F4F7FB]"
                }`}
              >
                <LayoutGrid size={15} /> Cards
              </button>
              <button
                onClick={() => setView("lista")}
                data-testid="view-lista-btn"
                className={`flex items-center gap-1.5 px-3 h-9 text-sm font-medium transition-colors ${
                  view === "lista" ? "bg-[#003882] text-white" : "bg-white text-[#335480] hover:bg-[#F4F7FB]"
                }`}
              >
                <List size={15} /> Lista
              </button>
            </div>

            {/* Novo quadro (PRO) */}
            {isPro ? (
              <Button
                variant="outline"
                onClick={() => setShowBoardModal(true)}
                className="border-[#003882] text-[#003882] hover:bg-[#e5edf5] h-9"
                data-testid="new-board-btn"
              >
                <Plus size={16} /> Novo quadro
              </Button>
            ) : (
              <Link to="/plano">
                <Button
                  variant="outline"
                  className="border-[#D1DEEE] text-[#6685AD] hover:border-[#003882] hover:text-[#003882] h-9"
                  data-testid="new-board-upgrade-btn"
                >
                  <Crown size={15} /> Mais quadros no PRO
                </Button>
              </Link>
            )}

            <Button
              onClick={() => openCreate()}
              className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold h-9"
              data-testid="new-card-btn"
            >
              <Plus size={16} /> Novo card
            </Button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="w-72 flex-shrink-0">
                <div className="h-5 w-28 bg-[#F4F7FB] rounded animate-pulse mb-3" />
                <div className="bg-[#F4F7FB] rounded-lg p-2 space-y-2">
                  <div className="h-16 bg-white border border-[#D1DEEE] rounded-lg animate-pulse" />
                  <div className="h-16 bg-white border border-[#D1DEEE] rounded-lg animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : view === "cards" ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveCard(null)}
          >
            <div className="flex gap-4 overflow-x-auto pb-4 items-start">
              {stages.map((stage, i) => (
                <KanbanColumn
                  key={stage.stage_id}
                  stage={stage}
                  color={stageColor(stage, i)}
                  cards={cardsByStage[stage.stage_id] || []}
                  onOpen={openEdit}
                  onToggleConcluido={toggleConcluido}
                  onAddCard={openCreate}
                  isPro={isPro}
                  onChangeColor={changeStageColor}
                />
              ))}
            </div>
            <DragOverlay dropAnimation={{ duration: 180, easing: "cubic-bezier(0.25, 1, 0.5, 1)" }}>
              {activeCard && (
                <div className="w-72 rotate-2">
                  <KanbanCard card={activeCard} onOpen={() => {}} onToggleConcluido={() => {}} dragging />
                </div>
              )}
            </DragOverlay>
          </DndContext>
        ) : (
          /* Lista view */
          <div className="bg-white border border-[#D1DEEE] rounded-lg overflow-hidden">
            {listaCards.length === 0 ? (
              <div className="p-12 text-center text-[#335480]">
                <LayoutGrid size={32} className="mx-auto mb-3 opacity-40" />
                <p className="mb-4">Nenhum card ainda</p>
                <Button onClick={() => openCreate()} className="bg-[#003882] hover:bg-[#00285e] text-white">
                  <Plus size={16} /> Criar primeiro card
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#D1DEEE] text-left text-[#335480]">
                      <th className="px-4 py-3 font-medium w-10"></th>
                      <th className="px-4 py-3 font-medium">Título</th>
                      <th className="px-4 py-3 font-medium">Etapa</th>
                      <th className="px-4 py-3 font-medium">Data desejada</th>
                      <th className="px-4 py-3 font-medium">Data limite (fatal)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#D1DEEE]">
                    {listaCards.map((c) => {
                      const overdue = !c.concluido && isOverdue(c.data_limite);
                      return (
                        <tr
                          key={c.card_id}
                          onClick={() => openEdit(c)}
                          className="hover:bg-[#F4F7FB] cursor-pointer transition-colors"
                          data-testid={`lista-row-${c.card_id}`}
                        >
                          <td className="px-4 py-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleConcluido(c); }}
                              aria-label={c.concluido ? "Reabrir" : "Concluir"}
                              className={`w-4 h-4 rounded-sm border flex items-center justify-center transition-colors ${
                                c.concluido ? "bg-[#003882] border-[#003882]" : "border-[#6685AD] hover:border-[#003882]"
                              }`}
                            >
                              {c.concluido && <Check size={12} className="text-white" strokeWidth={3} />}
                            </button>
                          </td>
                          <td className={`px-4 py-3 font-medium ${c.concluido ? "text-[#6685AD] line-through" : "text-[#001533]"}`}>
                            {c.title}
                          </td>
                          <td className="px-4 py-3 text-[#335480]">
                            <span className="inline-flex items-center gap-2">
                              {c.stageColor && (
                                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: c.stageColor }} />
                              )}
                              {c.stageName}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#335480]">{c.data_desejada ? formatDateBR(c.data_desejada) : "—"}</td>
                          <td className="px-4 py-3">
                            {c.data_limite ? (
                              <span className={`inline-flex items-center gap-1 font-semibold px-1.5 py-0.5 rounded text-xs ${
                                overdue ? "bg-[#003882] text-white" : "bg-[#e5edf5] text-[#003882]"
                              }`}>
                                <AlertCircle size={12} /> {formatDateBR(c.data_limite)}
                              </span>
                            ) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Nota do plano FREE */}
        {!isPro && !loading && (
          <p className="text-[#6685AD] text-sm mt-6">
            Plano FREE: 1 quadro com etapas padrão.{" "}
            <Link to="/plano" className="text-[#003882] font-medium hover:underline">
              Assine o PRO
            </Link>{" "}
            para criar mais quadros e personalizar etapas.
          </p>
        )}
      </div>

      {/* Modal card */}
      <Dialog open={showCardModal} onOpenChange={setShowCardModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Cabinet Grotesk" }}>
              {editingCard ? "Editar card" : "Novo card"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveCard} className="space-y-4">
            <div>
              <Label htmlFor="kb-title">Título *</Label>
              <Input
                id="kb-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Ex.: Elaborar contestação — caso Silva"
                autoFocus
                data-testid="card-title-input"
              />
            </div>
            <div>
              <Label htmlFor="kb-desc">Descrição</Label>
              <Textarea
                id="kb-desc"
                value={form.descricao}
                onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                placeholder="Detalhes, links, contexto…"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prioridade</Label>
                <select value={form.prioridade} onChange={(e) => setForm((f) => ({ ...f, prioridade: e.target.value }))} className="mt-1.5 h-10 w-full rounded-lg border border-[#D1DEEE] px-3 text-sm text-[#001533] bg-white">
                  <option value="">Sem prioridade</option>
                  {Object.entries(PRIORITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
              <div>
                <Label>Responsável</Label>
                <select value={form.responsavel_id} onChange={(e) => setForm((f) => ({ ...f, responsavel_id: e.target.value }))} className="mt-1.5 h-10 w-full rounded-lg border border-[#D1DEEE] px-3 text-sm text-[#001533] bg-white">
                  <option value="">Não atribuído</option>
                  {members.map((member) => <option key={member.user_id} value={member.user_id}>{member.name || member.email}</option>)}
                </select>
              </div>
            </div>
            {editingCard && (editingCard.processo_id || editingCard.intimacao_id) && (
              <div className="rounded-lg border border-[#D1DEEE] bg-[#F4F7FB] px-3 py-2 text-sm text-[#335480]">
                {editingCard.processo_id && <p>Processo vinculado: <strong className="text-[#001533]">{editingCard.processo_id}</strong></p>}
                {editingCard.intimacao_id && <p>Origem: <strong className="text-[#001533]">Intimação vinculada</strong></p>}
              </div>
            )}
            {members.length === 0 && <p className="text-xs text-[#6685AD]">Responsáveis indisponíveis no momento; a tarefa ficará não atribuída.</p>}
            {!editingCard && (
              <div>
                <Label>Etapa</Label>
                <Select value={form.stage_id} onValueChange={(v) => setForm((f) => ({ ...f, stage_id: v }))}>
                  <SelectTrigger className="border-[#D1DEEE]">
                    <SelectValue placeholder="Etapa" />
                  </SelectTrigger>
                  <SelectContent>
                    {stages.map((s) => (
                      <SelectItem key={s.stage_id} value={s.stage_id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="kb-desejada">Data desejada</Label>
                <Input
                  id="kb-desejada"
                  type="date"
                  value={form.data_desejada}
                  onChange={(e) => setForm((f) => ({ ...f, data_desejada: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="kb-limite">Data limite (fatal)</Label>
                <Input
                  id="kb-limite"
                  type="date"
                  value={form.data_limite}
                  onChange={(e) => setForm((f) => ({ ...f, data_limite: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              {editingCard ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={deleteCard}
                  disabled={saving}
                  className="text-[#6685AD] hover:text-[#003882] px-2"
                  data-testid="card-delete-btn"
                >
                  <Trash2 size={16} /> Excluir
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCardModal(false)} className="border-[#D1DEEE]">
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving || !form.title.trim()}
                  className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
                  data-testid="card-save-btn"
                >
                  {saving ? "Salvando…" : editingCard ? "Salvar" : "Criar card"}
                </Button>
              </div>
            </div>
          </form>
          {editingCard && <TaskActivity {...activity} onComment={addComment} onEditComment={editComment} onDeleteComment={deleteComment} onLoadMore={loadMoreComments} />}
        </DialogContent>
      </Dialog>

      {/* Modal novo quadro (PRO) */}
      <Dialog open={showBoardModal} onOpenChange={setShowBoardModal}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Cabinet Grotesk" }}>Novo quadro</DialogTitle>
          </DialogHeader>
          <form onSubmit={createBoard} className="space-y-4">
            <div>
              <Label htmlFor="kb-board-name">Nome do quadro *</Label>
              <Input
                id="kb-board-name"
                value={boardName}
                onChange={(e) => setBoardName(e.target.value)}
                placeholder="Ex.: Contencioso cível"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowBoardModal(false)} className="border-[#D1DEEE]">
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving || !boardName.trim()}
                className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
              >
                {saving ? "Criando…" : "Criar quadro"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
